// Multiplication Garden generator: arrays as rows of seeds.

import { int, type Rng } from '@kit/rng';

export type ArrayKind = 'total' | 'missing-rows' | 'area' | 'division' | 'split';

export interface ArrayProblem {
  kind: ArrayKind;
  rows: number;
  cols: number;
  /** what she must answer */
  answer: number;
  /** the seed packet text */
  text: string;
  /** hint toward the answer */
  hint: string;
  /** 4 choices at L1-2 */
  choices?: number[];
  /** for 'split': cols = 10 + extra, shown as two plots */
  split?: { a: number; b: number };
  /** whether she plants rows herself (true) or the plot is pre-planted (area) */
  plant: boolean;
}

function choicesFor(answer: number, rng: Rng): number[] {
  const set = new Set<number>([answer]);
  let g = 0;
  while (set.size < 4 && g++ < 60) { const v = answer + int(rng, -4, 4); if (v > 0 && v !== answer) set.add(v); }
  while (set.size < 4) set.add(answer + set.size * 2);
  const arr = [...set];
  for (let i = arr.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [arr[i], arr[j]] = [arr[j], arr[i]]; }
  return arr;
}

export function makeArray(level: number, rng: Rng): ArrayProblem {
  const L = Math.max(1, Math.min(10, level));
  if (L <= 2) {
    const rows = int(rng, 2, 5), cols = int(rng, 2, 5);
    return { kind: 'total', rows, cols, answer: rows * cols, text: `${rows} rows of ${cols}`, hint: `Count by ${cols}s: ${Array.from({ length: rows }, (_, i) => (i + 1) * cols).join(', ')}.`, choices: choicesFor(rows * cols, rng), plant: true };
  }
  if (L <= 4) {
    const rows = int(rng, 2, 10), cols = int(rng, 2, 10);
    return { kind: 'total', rows, cols, answer: rows * cols, text: `${rows} rows of ${cols}`, hint: `${rows} times ${cols}. Count by ${cols}s, ${rows} times.`, plant: true };
  }
  if (L <= 6) {
    const cols = int(rng, 3, 9), rows = int(rng, 2, 9);
    return { kind: 'missing-rows', rows, cols, answer: rows, text: `? rows of ${cols} make ${rows * cols}`, hint: `Plant rows of ${cols} until you reach ${rows * cols}. ${rows * cols} divided by ${cols}.`, plant: true };
  }
  if (L <= 8) {
    if (rng() < 0.5) {
      const rows = int(rng, 3, 9), cols = int(rng, 3, 12);
      return { kind: 'area', rows, cols, answer: rows * cols, text: `A plot ${cols} wide and ${rows} tall. How many square units?`, hint: `Area is width times height: ${cols} times ${rows}.`, plant: false };
    }
    const cols = int(rng, 3, 9), rows = int(rng, 3, 9);
    return { kind: 'division', rows, cols, answer: rows, text: `${rows * cols} seeds in rows of ${cols}. How many rows?`, hint: `Keep planting rows of ${cols}. Each row uses ${cols} seeds.`, plant: true };
  }
  const b = int(rng, 1, 9), rows = int(rng, 3, 9);
  const cols = 10 + b;
  return { kind: 'split', rows, cols, answer: rows * cols, text: `${rows} rows of ${cols}`, hint: `Split it: ${rows} x 10 = ${rows * 10} and ${rows} x ${b} = ${rows * b}. Add them.`, split: { a: 10, b }, plant: false };
}
